/**
 * The scoring vocabulary for interview scorecards, shared by the Recruiting
 * tab and the printed report.
 *
 * It lives here rather than in the page because the report has to describe a
 * result in exactly the terms the interviewer saw on screen. Two copies of the
 * thresholds would eventually disagree, and the copy that disagreed would be
 * the one printed and taken into a debrief.
 *
 * The source app used a green / amber / red band palette. This brand has
 * neither green nor amber, so the bands ride the same ladder as every other
 * status in the app: navy for fine, climbing the crimson ramp as it gets
 * worse. A "Strong hire" therefore does not light up — it simply is not
 * crimson, which is what "good" looks like here.
 */

/** The rating anchors. Shared by every role so a "4" means the same thing
 *  across the business — the per-question guidance is what makes it
 *  role-specific. */
export const SCALE = [
  { value: 1, label: 'Well below', blurb: 'No relevant evidence, or answer raised a concern.' },
  { value: 2, label: 'Below', blurb: 'Some awareness, but thin or needed heavy prompting.' },
  { value: 3, label: 'Meets', blurb: 'Solid, credible answer. Would cope in the role.' },
  { value: 4, label: 'Above', blurb: 'Strong, specific, drawn from real experience.' },
  { value: 5, label: 'Exceptional', blurb: 'Taught the interviewer something. Clear top decile.' },
] as const;

/** A question's importance. Critical counts triple a nice-to-have, so a weak
 *  answer where it matters costs more than a weak answer where it does not. */
export const WEIGHTS: Record<number, { label: string; badge: string }> = {
  1: { label: 'Nice to have', badge: 'badge-neutral' },
  2: { label: 'Important', badge: 'badge-good' },
  3: { label: 'Critical', badge: 'badge-serious' },
};

/**
 * Bands for the weighted percentage, so a total turns into a decision rather
 * than a number people interpret differently. The blurb IS the decision — a
 * threshold nobody can act on is just a colour.
 *
 * `ink` and `line` are for the printed report, which has no access to the
 * app's CSS variables: it is rendered into a detached iframe as a standalone
 * document. They are the literal token values, quoted from tokens.css.
 */
export const BANDS = [
  {
    min: 85,
    label: 'Strong hire',
    badge: 'badge-good',
    ink: '#14395C',
    line: '#C5D4E2',
    wash: '#F4F7FA',
    blurb: 'Proceed to offer. Would raise the bar for the team.',
  },
  {
    min: 70,
    label: 'Hire',
    badge: 'badge-good',
    ink: '#23517B',
    line: '#C5D4E2',
    wash: '#F4F7FA',
    blurb: 'Proceed, with a documented ramp plan on the weakest competency.',
  },
  {
    min: 55,
    label: 'Borderline',
    badge: 'badge-warning',
    ink: '#8B1319',
    line: '#F4B3B6',
    wash: '#FDEDED',
    blurb: 'Hold. Only proceed if the gap is coachable with a named internal owner.',
  },
  {
    min: 0,
    label: 'Do not hire',
    badge: 'badge-critical',
    ink: '#FFFFFF',
    line: '#AE1920',
    wash: '#AE1920',
    blurb: 'No hire, regardless of how strong the rest of the interview felt.',
  },
] as const;

export type Band = (typeof BANDS)[number];

/** The last band is `min: 0`, so this always matches. */
export const bandFor = (pct: number): Band => BANDS.find((b) => pct >= b.min) ?? BANDS[BANDS.length - 1]!;

/** The range each band covers, derived rather than written down twice so
 *  moving a threshold cannot leave the printed range lying about it. */
export function bandRange(i: number): string {
  if (i === 0) return `${BANDS[0].min}% and above`;
  if (BANDS[i]!.min === 0) return `Below ${BANDS[i - 1]!.min}%`;
  return `${BANDS[i]!.min}–${BANDS[i - 1]!.min - 1}%`;
}

export interface ScoreRow {
  category: string;
  weight: number;
  rating: number;
  text: string;
}

export interface CategoryBreakdown {
  category: string;
  n: number;
  got: number;
  max: number;
  weight: number;
  /** The weighted mean rating, on the same 1-5 scale the interviewer used. */
  score: number;
  /** This competency's share of the points on offer, as a percentage. */
  share: number;
}

/**
 * Rolls the rated questions up by category — the competency view a scorecard
 * is actually read in.
 *
 * Takes `{category, weight, rating}` rows so the live scorecard and a saved
 * one, which store them differently, share the maths.
 *
 * `score` is the weighted mean rating: sum(rating x weight) / sum(weight). It
 * stays on the 1-5 scale the interviewer just used, which the percentage does
 * not, and it is the number a competency threshold gets compared against.
 */
export function breakdown(rows: ScoreRow[]): CategoryBreakdown[] {
  const byCategory = new Map<string, Omit<CategoryBreakdown, 'score' | 'share'>>();
  for (const r of rows) {
    const c = byCategory.get(r.category) ?? { category: r.category, n: 0, got: 0, max: 0, weight: 0 };
    c.n += 1;
    c.got += r.rating * r.weight;
    c.max += 5 * r.weight;
    c.weight += r.weight;
    byCategory.set(r.category, c);
  }
  const cats = [...byCategory.values()];
  const totalMax = cats.reduce((n, c) => n + c.max, 0);
  return cats.map((c) => ({
    ...c,
    score: c.got / c.weight,
    share: totalMax ? Math.round((c.max / totalMax) * 100) : 0,
  }));
}

/** A weak answer on a Critical question is what a good total hides, so both
 *  the page and the report call it out separately. */
export const criticalConcerns = (rows: ScoreRow[]): ScoreRow[] =>
  rows.filter((r) => r.weight === 3 && r.rating < 3);

/**
 * How a rating reads. Deliberately banded with `>=` rather than `===`, because
 * competency scores are weighted MEANS — an `=== 3` test would paint a 3.9 as
 * a concern and a 3.0 as adequate.
 */
export const ratingClass = (rating: number): string =>
  rating >= 4 ? 'strong' : rating >= 3 ? 'muted' : 'flagged';

/* =========================================================================
   The weighted competency model, for roles that define one

   Everything above scores QUESTIONS and bands the result as a percentage.
   That is the right model for a single conversation against a flat bank.

   A staged process is decided differently, and the difference matters. The
   hire is judged on COMPETENCIES, each carrying a share of the decision that
   sums to 100; the questions are only how evidence is gathered. Two things
   follow that the percentage model cannot express:

     - A competency is scored on the 1-5 scale, not converted to a percentage,
       because the thresholds a hiring panel argues over are written in those
       terms: "4.0 weighted, nothing below 3".

     - Some competencies are HARD GATES. Below 3 there is a no-hire whatever
       the average says — and the average is exactly what would hide it, since
       a 1 in a 20% competency still leaves a comfortable-looking 3.8.

   Only competencies with at least one rated question count. An interviewer
   who scores one stage has not scored the whole model, and renormalising over
   what they did score is what makes a partial scorecard honest rather than
   punishing the candidate for the questions nobody asked.
   ========================================================================= */

export interface CompetencyInput {
  ref: string;
  name: string;
  weightPct: number;
  isGate: boolean;
}

/** One rated answer, tagged with the competency it informs. */
export interface RefScoreRow {
  ref: string | null;
  weight: number;
  rating: number;
}

export interface CompetencyScore extends CompetencyInput {
  /** Questions rated against it in this scorecard. */
  n: number;
  /** Weighted mean rating, 1-5. Null where nothing was rated. */
  score: number | null;
}

export interface WeightedOutcome {
  competencies: CompetencyScore[];
  /** Weighted mean on the 1-5 scale, over scored competencies only. */
  weighted: number | null;
  /** How much of the model this scorecard actually covers, as a percentage.
   *  A stage-1 screen legitimately covers a fraction of it. */
  covered: number;
  /** Scored gates sitting below 3 — each one is a no-hire on its own. */
  gateFailures: CompetencyScore[];
  /** Every scored competency below 3, gate or not. */
  below3: CompetencyScore[];
  decision: Decision;
}

export interface Decision {
  label: string;
  badge: string;
  blurb: string;
}

/* The thresholds as the matrix writes them. Written once, here, so the screen
   and the exported scorecard cannot disagree about what a number means. */
const HIRE: Decision = {
  label: 'Hire',
  badge: 'badge-good',
  blurb: 'Hire. Consider whether the scope should be broader than advertised.',
};
const HIRE_RAMP: Decision = {
  label: 'Hire with a ramp plan',
  badge: 'badge-good',
  blurb:
    'Hire with a documented ramp plan on the weakest competency and an executive sponsor for six months.',
};
const HOLD: Decision = {
  label: 'Hold',
  badge: 'badge-warning',
  blurb:
    'Hold. Proceed only if the gap is a supporting competency and there is a named internal owner to cover it while they ramp.',
};
const NO_HIRE: Decision = {
  label: 'Do not hire',
  badge: 'badge-critical',
  blurb: 'No hire. The weighted outcome is below the bar for the role.',
};
const gateFail = (c: CompetencyScore): Decision => ({
  label: 'Do not hire',
  badge: 'badge-critical',
  blurb: `No hire. ${c.ref}. ${c.name} is a hard gate and scored below 3 — it is the job.`,
});
const NOT_SCORED: Decision = {
  label: 'Not yet scored',
  badge: 'badge-neutral',
  blurb: 'Rate at least one question to see where this lands.',
};

export function weightedOutcome(
  rows: RefScoreRow[],
  model: CompetencyInput[],
): WeightedOutcome {
  const competencies: CompetencyScore[] = model.map((c) => {
    const mine = rows.filter((r) => r.ref === c.ref);
    const w = mine.reduce((n, r) => n + r.weight, 0);
    return {
      ...c,
      n: mine.length,
      /* Weighted by the QUESTION weight inside the competency — a critical
         question moves its competency more than a nice-to-have one does. */
      score: w > 0 ? mine.reduce((n, r) => n + r.rating * r.weight, 0) / w : null,
    };
  });

  const scored = competencies.filter((c) => c.score !== null);
  const totalPct = scored.reduce((n, c) => n + c.weightPct, 0);
  const weighted =
    totalPct > 0 ? scored.reduce((n, c) => n + c.score! * c.weightPct, 0) / totalPct : null;

  const below3 = scored.filter((c) => c.score! < 3);
  const gateFailures = below3.filter((c) => c.isGate);
  const covered = Math.round(totalPct);

  let decision: Decision;
  if (weighted === null) {
    decision = NOT_SCORED;
  } else if (gateFailures.length > 0) {
    /* Named rather than generic: "no hire" without saying which gate failed
       is the kind of verdict a debrief spends twenty minutes reconstructing. */
    decision = gateFail(gateFailures[0]!);
  } else if (weighted >= 4 && below3.length === 0) {
    decision = HIRE;
  } else if (weighted >= 3.5 && below3.length === 0) {
    decision = HIRE_RAMP;
  } else if (weighted >= 3.5) {
    decision = HOLD;
  } else {
    decision = NO_HIRE;
  }

  return { competencies, weighted, covered, gateFailures, below3, decision };
}

/** The thresholds, for printing beside a result so the reader can check it. */
export const WEIGHTED_THRESHOLDS: { when: string; then: string }[] = [
  { when: '4.0 or above, nothing below 3', then: 'Hire. Consider a broader scope than advertised.' },
  { when: '3.5–3.9, nothing below 3', then: 'Hire with a ramp plan and an executive sponsor.' },
  { when: '3.5 or above, one competency at 2', then: 'Hold. Only with a named owner covering the gap.' },
  { when: 'A hard-gate competency below 3', then: 'No hire, whatever the average says.' },
  { when: 'Below 3.5', then: 'No hire.' },
];
